import * as moment from "moment";
import * as React from "react";
import BackButton from "../../core/BackButton/BackButton";
import * as Constants from "../../core/constants";
import Spinner from "../../core/Spinner/Spinner";
import { ITranslationProps, translateComponent } from "../../helpers/translation";
import IListResults from "../../types/IListResults";
import IMessageHeader from "../../types/IMessageHeader";
import IRecipient from "../../types/IRecipient";
import IRecipientSearchRequest from "../../types/IRecipientSearchRequest";
import IResponse from "../../types/IResponse";
import "./MessageDetail.css";
import RecipientList from "./RecipientList";

interface IMessageDetailProps extends ITranslationProps {
    loading: boolean;
    loadingRecipients: boolean;
    message: IMessageHeader;
    searchRecipients: (request: IRecipientSearchRequest) => Promise<IResponse<IListResults<IRecipient>> | null>;
}

class MessageDetail extends React.Component<IMessageDetailProps> {

    constructor(props: IMessageDetailProps) {
        super(props);

        this.getEmailFields = this.getEmailFields.bind(this);
        this.getVoiceFields = this.getVoiceFields.bind(this);
    }

    public render() {
        return <div>
            <BackButton />

            {this.props.loading && <Spinner />}

            <h2>{this.props.t("MessageDetail.Title")}</h2>

            <div className="card mt-3">
                <div className="card-header">
                    {this.props.t("MessageDetail.MessageInformation")}
                </div>
                <div className="card-body message-detail">
                    <div className="row">
                        <div className="col-md-2"><strong>{this.props.t("MessageDetail.MessageDate")}</strong></div>
                        <div className="col-md-4">{this.props.message.dateTimeCreated ? moment(this.props.message.dateTimeCreated).format(Constants.MOMENT_FORMAT_DATETIME) : ""}</div>

                        <div className="col-md-2"><strong>{this.props.t("MessageDetail.MessageType")}</strong></div>
                        <div className="col-md-4">{this.props.message.messageType}</div>

                        <div className="col-md-2"><strong>{this.props.t("MessageDetail.ReferenceID")}</strong></div>
                        <div className="col-md-4">{this.props.message.referenceID}</div>

                        <div className="col-md-2"><strong>{this.props.t("MessageDetail.ScheduledDateTime")}</strong></div>
                        <div className="col-md-4">{this.props.message.utcScheduledDateTime ? moment(this.props.message.utcScheduledDateTime).format(Constants.MOMENT_FORMAT_DATETIME) : ""}</div>

                        <div className="col-md-2"><strong>{this.props.t("MessageDetail.IsTest")}</strong></div>
                        <div className="col-md-4">{this.props.message.isTest ? this.props.t("Global.Yes") : this.props.t("Global.No")}</div>

                        <div className="col-md-2"><strong>{this.props.t("MessageDetail.UniqueID")}</strong></div>
                        <div className="col-md-4">{this.props.message.uniqueID}</div>

                        <div className="col-md-2"><strong>{this.props.t("MessageDetail.PostbackURL")}</strong></div>
                        <div className="col-md-4">{this.props.message.statusChangePostURL}</div>
                    </div>

                    {this.getEmailFields()}

                    {this.getVoiceFields()}

                    <div className="row">
                        <div className="col-md-2"><strong>{this.props.t("MessageDetail.Description")}</strong></div>
                        <div className="col-md-10">{this.props.message.description}</div>
                    </div>
                    <div className="row">
                        <div className="col-md-2"><strong>{this.props.t("MessageDetail.MessageText")}</strong></div>
                        <div className="col-md-10">
                            {
                                this.props.message.isHtmlFormat ?
                                    <pre>{this.props.message.messageContent}</pre> : this.props.message.messageContent
                            }
                        </div>
                    </div>

                </div>

            </div>

            {this.props.message.id > 0 &&
                <RecipientList message={this.props.message}
                    loading={this.props.loadingRecipients}
                    search={this.props.searchRecipients} />
            }

        </div>;
    }

    private getEmailFields(): React.ReactNode | null {
        if (this.props.message.messageType !== Constants.MESSAGE_TYPE_EMAIL) {
            return null;
        }

        const attachments: React.ReactNode[] = [];
        if (this.props.message.attachments) {
            this.props.message.attachments.forEach(attachment => {
                attachments.push(<div>{attachment.originalFileName}</div>);
            });
        }

        const result: React.ReactNode = <div>
            <div className="row">
                <div className="col-md-2"><strong>{this.props.t("MessageDetail.Subject")}</strong></div>
                <div className="col-md-4">{this.props.message.subject}</div>

                <div className="col-md-2"><strong>{this.props.t("MessageDetail.From")}</strong></div>
                <div className="col-md-4">{this.props.message.from}</div>

                <div className="col-md-2"><strong>{this.props.t("MessageDetail.HasAttachments")}</strong></div>
                <div className="col-md-4">{this.props.message.hasAttachments ? this.props.t("Global.Yes") : this.props.t("Global.No")}</div>

                {this.props.message.hasAttachments && <div className="col-md-2"><strong>{this.props.t("MessageDetail.Attachments")}</strong></div>}
                {this.props.message.hasAttachments && <div className="col-md-4">{attachments}</div>}
            </div>
        </div>;

        return result;
    }

    private getVoiceFields(): React.ReactNode | null {

        if (this.props.message.messageType !== Constants.MESSAGE_TYPE_VOICE) {
            return null;
        }

        const result: React.ReactNode = <div>
            <div className="row">
                <div className="col-md-2"><strong>{this.props.t("MessageDetail.VoiceID")}</strong></div>
                <div className="col-md-4">{this.props.message.voiceID}</div>

                <div className="col-md-2"><strong>{this.props.t("MessageDetail.SoundFile")}</strong></div>
                <div className="col-md-4">{this.props.message.soundFileID}</div>

                <div className="col-md-2"><strong>{this.props.t("MessageDetail.ResponseTextToSay")}</strong></div>
                <div className="col-md-4">{this.props.message.responseTextToSay}</div>

                <div className="col-md-2"><strong>{this.props.t("MessageDetail.MachineAnswerTextToSay")}</strong></div>
                <div className="col-md-4">{this.props.message.machineAnswerTextToSay}</div>

                <div className="col-md-2"><strong>{this.props.t("MessageDetail.ValidResponses")}</strong></div>
                <div className="col-md-4">{this.props.message.responses ? this.props.message.responses.split("|").join(", ") : ""}</div>
            </div>

        </div>;

        return result;
    }
}

export default translateComponent(MessageDetail);



// WEBPACK FOOTER //
// ./src/components/MessageDetail/MessageDetail.tsx